import { useState, useEffect } from "react";
import { createModel } from "hox";
import { TablePaginationConfig } from "antd/es/table/interface";
import { OptionDsType, ISearchPage } from "@/types/FmsTypes";
import { DEFAULT_FIRST_PAGE_NUM, DEFAULT_PAGE_SIZE, } from "@/utils/constants";
import { IStatistics, stockStatisticService } from "./stockStatisticService";
import { warehouseService } from "./warehouseService";
import useGlobalModel from "@/model/globalModel";
import { transPgToPagination } from "@/views/base/app/appModel";
import { globalPrompt } from "@/components/YhMessage";
import { ISCMStockStatisticSearchField, ISCMStockStatisticDetail, ISCMStockMoveDetail, ISCMStockMoveSearchField, ISCMShiftShelf } from "./stockStatisticTypes";
import { convertRes2Blob, convertRes2Blob2WithFileName } from "@/utils/exportFile";
import { useLockFn } from "ahooks";
import { getRandomStr } from "@/utils/utils";
import useStorageState from '../../../hooks/useStorageState';


export const useStockStatisticModel = createModel(function () {
    const { user: { shopId }, setLoadingInfo, userBranch } = useGlobalModel();
    const initPgCfg: TablePaginationConfig = { pageSize: DEFAULT_PAGE_SIZE, current: DEFAULT_FIRST_PAGE_NUM }

    //查询条件
    const [searchParam, setSearchParam] = useState<ISCMStockStatisticSearchField>({});
    //列表数据源
    const [dataSource, setDataSource] = useState<ISCMStockStatisticDetail[]>([]);
    //表格选中行Key值
    const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);
    //列表数据源分页参数
    const [pgCfg, setPgCfg] = useState<TablePaginationConfig>(initPgCfg);
    //列表统计展示数据
    const initStatistics = {
        totalInventoryAmount: 0,
        totalInventoryNum: 0,
        stockNum: 0,
        stockAmount: 0,
    };
    const [statistics, setStatistics] = useState<IStatistics>(initStatistics);
    const [loading, setLoading] = useState(false);

    //占用库存可查明细 弹框
    const [stockQueryModal, setStockQueryModal] = useState<ISCMStockStatisticDetail | null>(null);

    const updateSearchParam = (updateParam: ISCMStockStatisticSearchField) => {
        setSearchParam({ ...searchParam, ...updateParam })
    }

    //切换门店重置缓存
    useEffect(() => {
        setSearchParam({});
        setSelectedRowKeys([]);
    }, [shopId]);

    //重置
    const resetSearchParam = () => {
        setSearchParam({});
        setSelectedRowKeys([]);
        loadData(initPgCfg, {});
    }

    const loadData = async (page: ISearchPage = initPgCfg, searchValue: ISCMStockStatisticSearchField = searchParam) => {
        setLoading(true);
        const searchParams = {
            shopId,
            ...searchValue,
            materialName: searchValue?.materialName?.trim() ?? undefined,
            materialCode: searchValue?.materialCode?.trim() ?? undefined,
            replaceCode: searchValue?.replaceCode?.trim() ?? undefined,
            pageSize: page?.pageSize ?? pgCfg.pageSize ?? DEFAULT_PAGE_SIZE,
            pageNum: (page?.current || page?.pageNum) ?? pgCfg.current ?? DEFAULT_FIRST_PAGE_NUM,
        }
        if (userBranch === 'scm') {
            // let { retData } = await stockStatisticService.scmPage(searchParams);
            // let { retData: Data } = await stockStatisticService.scmStatistics({ ...searchParams, pageSize: -1 });
            // const { records, ...pg } = retData;
            // setPgCfg(transPgToPagination(pg));
            // setDataSource(records.map(item => ({
            //     ...item,
            //     key: getRandomStr()
            // })));
            // setStatistics(Data);
            setDataSource([]);
            // setStatistics([]);
        } else if (userBranch === 'vrm') {
            let { retData } = await stockStatisticService.vrmPage(searchParams);
            let { retData: Data } = await stockStatisticService.vrmStatistics({ ...searchParams, pageSize: -1 });
            const { records, ...pg } = retData[0].list;
            setPgCfg(transPgToPagination(pg));
            setDataSource(records.map(item => ({
                ...item,
                key: getRandomStr()
            })));
            setStatistics(Data);
        } else if (userBranch === 'bcm') {
            let { retData } = await stockStatisticService.bcmPage(searchParams);
            let { retData: Data } = await stockStatisticService.bcmStatistics({ ...searchParams, pageSize: -1 });
            const { records, ...pg } = retData[0].list;
            setPgCfg(transPgToPagination(pg));
            setDataSource(records.map(item => ({
                ...item,
                key: getRandomStr()
            })));
            setStatistics(Data);
        } else if (userBranch === 'ci') {
            let { retData } = await stockStatisticService.ciPage(searchParams);
            let { retData: Data } = await stockStatisticService.ciStatistics({ ...searchParams, pageSize: -1 });
            const { records, ...pg } = retData;
            setPgCfg(transPgToPagination(pg));
            setDataSource(records.map(item => ({
                ...item,
                key: getRandomStr()
            })));
            setStatistics(Data);
        }
        setLoading(false);
    }

    // const loadTotalAmount = async () => {
    //     let { retData } = await stockStatisticService.totalAmount(shopId);
    //     setStatistics(retData ?? initStatistics);
    // }

    //导出
    const exportStockStatistic = async (searchValue: ISCMStockStatisticSearchField = searchParam) => {
        setLoadingInfo({ loadingVisible: true, txt: "快速导出中" });
        try {
            const searchParams = {
                shopId,
                ...searchValue,
                pageSize: -1,
                pageNum: 1,
            }
            let result = await stockStatisticService.export(searchParams, userBranch);
            result && setLoadingInfo({ loadingVisible: false, txt: "" });
            if (userBranch === 'ci') {
                convertRes2Blob2WithFileName(result, "库存统计.xlsx");
            }
            else {
                convertRes2Blob(result);
            }
        } catch { setLoadingInfo({ loadingVisible: false, txt: "" }) }
    }
    //核验导出
    const exportStockCheck = async () => {
        setLoadingInfo({ loadingVisible: true, txt: "库存核验快速导出中" });
        try {
            const searchParams = {
                shopId,
            }
            let result = await stockStatisticService.statisticCheck(searchParams);
            result && setLoadingInfo({ loadingVisible: false, txt: "" });
            convertRes2Blob(result);
        } catch { setLoadingInfo({ loadingVisible: false, txt: "" }) }
    }
    /**
     *  //转移货位
     */
    const [shiftShelfModal, setShiftShelfModal] = useState<boolean>(false);
    //查询条件
    const [searchParamShelf, setSearchParamShelf] = useState<ISCMStockStatisticSearchField>({});
    //列表数据源
    const [dataSourceShelf, setDataSourceShelf] = useState<ISCMStockStatisticDetail[]>([]);
    //列表数据源分页参数
    const [pgCfgShelf, setPgCfgShelf] = useState<TablePaginationConfig>(initPgCfg);
    //已选中的
    const [selectedShelfRows, setSelectedShelfRows] = useState<ISCMStockStatisticDetail[]>([]);

    const loadDataShelf = async (page?: ISearchPage, searchValue: ISCMStockStatisticSearchField = searchParamShelf) => {
        const searchParams = {
            shopId,
            isNotDirect: "1",
            pageSize: page?.pageSize ?? pgCfgShelf.pageSize ?? DEFAULT_PAGE_SIZE,
            pageNum: page?.pageNum ?? pgCfgShelf.current ?? DEFAULT_FIRST_PAGE_NUM,
            materialName: searchValue.materialName?.trim() ?? "",
            materialCode: searchValue.materialCode?.trim() ?? "",
        }
        if (userBranch === 'scm') {
            let { retData } = await stockStatisticService.scmPage(searchParams);
            const { records, ...pg } = retData;
            setPgCfgShelf(transPgToPagination(pg));
            setDataSourceShelf(records);
        } else if (userBranch === 'vrm') {
            let { retData } = await stockStatisticService.vrmPage(searchParams);
            const { records, ...pg } = retData[0].list;
            setPgCfgShelf(transPgToPagination(pg));
            setDataSourceShelf(records);
        } else if (userBranch === 'bcm') {
            let { retData } = await stockStatisticService.bcmPage(searchParams);
            const { records, ...pg } = retData[0].list;
            setPgCfg(transPgToPagination(pg));
            setPgCfgShelf(transPgToPagination(pg));
            setDataSourceShelf(records);
        } else if (userBranch === 'ci') {
            let { retData } = await stockStatisticService.ciPage(searchParams);
            const { records, ...pg } = retData;
            setPgCfgShelf(transPgToPagination(pg));
            setDataSourceShelf(records);
        }
    }
    const shiftShelfApi = useLockFn(async () => {
        if (!selectedShelfRows.length) {
            globalPrompt("message", { text: "请选择你要转移的货位", type: "warning" });
            return;
        }
        setLoadingInfo({ loadingVisible: true, txt: "正在转移货位" });
        let arr: ISCMShiftShelf[] = selectedShelfRows.map(item => ({
            id: item.id,
            warehouseCode: item.warehouseCode1,
            warehouseName: item.warehouseName1,
            shelfId: item.shelfId1,
            shelfCode: item.shelfCode1,
            moveNum: item.moveNum,

        }))
        let { retData } = await stockStatisticService.shiftShelf(arr);
        if (retData) {
            setShiftShelfModal(false);
            setSelectedShelfRows([]);
            loadData();
            setLoadingInfo({ loadingVisible: false, txt: "" });
            globalPrompt("message", { text: "货位转移成功", type: "success" })
        } else {
            globalPrompt("message", { text: "货位转移失败", type: "error" });
        }
    })

    const shelftDownloadTpl = async () => {
        //let result = await stockStatisticService.downloadTpl();
        //convertRes2Blob(result);
    }
    /**
     *  //转移记录
     */
    const [shiftRecordModal, setShiftRecordModal] = useState<boolean>(false);
    //查询条件
    const [searchParamMove, setSearchParamMove] = useState<ISCMStockMoveSearchField>({});
    //列表数据源
    const [dataSourceMove, setDataSourceMove] = useState<ISCMStockMoveDetail[]>([]);
    //表格选中行Key值
    const [selectedRowKeysMove, setSelectedRowKeysMove] = useState<string[]>([]);
    //列表数据源分页参数
    const [pgCfgMove, setPgCfgMove] = useState<TablePaginationConfig>(initPgCfg);

    const loadDataMove = async (page?: ISearchPage, searchValue: ISCMStockMoveSearchField = searchParamMove) => {
        const searchParams = {
            shopId,
            ...searchValue,
            pageSize: page?.pageSize ?? pgCfgMove.pageSize ?? DEFAULT_PAGE_SIZE,
            pageNum: page?.pageNum ?? pgCfgMove.current ?? DEFAULT_FIRST_PAGE_NUM,
        }
        let { retData } = await stockStatisticService.movePage(searchParams);
        const { records, ...pg } = retData;
        setPgCfgMove(transPgToPagination(pg));
        setDataSourceMove(records);
    }

    const reduction = async (id: string) => {
        let { retData, retMsg } = await stockStatisticService.reduction(id);
        if (retData) {
            loadDataMove();
            globalPrompt("message", { text: retMsg, type: "success" })
        } else {
            globalPrompt("message", { text: retMsg, type: "error" });
        }
    }

    //所有仓库
    const [whDs, setwhDs] = useStorageState<OptionDsType[]>("scm.waerhouseDs", []);

    //初始所有仓库
    const warehouseloadDataAll = async (shopId: string) => {
        if (userBranch === 'scm') {
            let { retData } = await warehouseService.scmPage({ pageSize: -1, pageNum: 1, shopId });
            const { records } = retData;
            setwhDs(records.map(item => ({
                id: item.id,
                label: item.name,
                value: item.code
            })));
        } else if (userBranch === 'vrm') {
            let { retData } = await warehouseService.vrmPage({ pageSize: -1, pageNum: 1, shopId });
            const { records } = retData;
            setwhDs(records.map(item => ({
                id: item.id,
                label: item.name,
                value: item.code
            })));
        } else if (userBranch === 'bcm') {
            let { retData } = await warehouseService.bcmPage({ pageSize: -1, pageNum: 1, shopId });
            const { records } = retData;
            setwhDs(records.map(item => ({
                id: item.id,
                label: item.name,
                value: item.code
            })));
        } else if (userBranch === 'ci') {
            let { retData } = await warehouseService.ciPage({ "pageSize": 10000, "pageNum": 1, "shopId": shopId });
            const { records } = retData;
            setwhDs(records.map(item => ({
                id: item.id,
                label: item.name,
                value: item.code
            })));
        }
    }

    return {
        /***field***/
        searchParam,
        dataSource,
        selectedRowKeys,
        pgCfg,
        statistics,
        loading,
        whDs,
        exportStockStatistic,
        /***method***/
        setDataSource,
        setSelectedRowKeys,
        resetSearchParam,
        updateSearchParam,
        loadData,
        // loadTotalAmount,
        /*****转移记录*******/
        shiftRecordModal, setShiftRecordModal,
        searchParamMove, setSearchParamMove,
        dataSourceMove,
        selectedRowKeysMove, setSelectedRowKeysMove,
        pgCfgMove, setPgCfgMove,
        loadDataMove, reduction, shelftDownloadTpl,
        /***** 转移货位*******/
        shiftShelfModal, setShiftShelfModal,
        searchParamShelf, setSearchParamShelf,
        dataSourceShelf,
        selectedShelfRows, setSelectedShelfRows,
        pgCfgShelf, setPgCfgShelf,
        loadDataShelf, shiftShelfApi,
        stockQueryModal, setStockQueryModal,
        exportStockCheck,
        warehouseloadDataAll
    }
});

